import {Section} from 'interface/base/Section';
import {SearchMenuItem} from 'interface/stacks/search/base/SearchMenuItem';
import {StyleSheet, View} from 'react-native';

import theme from 'config/theme';
import Typography from 'interface/base/Typography';
import {BaseSearch} from 'interface/stacks/search/views/BaseSearch';
import {Fragment} from 'react';
import {useSelector} from 'react-redux';
import {selectors as filesSelectors} from 'store/slices/files';
import type {FilesItem} from 'store/slices/files/types';
import {useCurrentFolder} from '../../../context/CurrentFolderContext';
import {AiChatCurrentFolderChip} from './AiChatCurrentFolderChip';

interface SearchProps {
  instanceNs: 'workspace' | 'share';
  instanceId: string;
  instanceAlt: string;
  instanceName: string;
  files: FilesItem[];
  onAdd: (file: FilesItem) => void;
  onRemove: (file: FilesItem) => void;
  filterForShowing: (file: FilesItem) => boolean;
  filterForEnabling: (file: FilesItem) => boolean;
  showCurrentFolder: boolean;
}

const isShare = (folderId: string) => {
  return folderId.startsWith('5');
};

export function AiChatSearch({
  instanceName,
  instanceId,
  instanceNs,
  instanceAlt,
  files,
  onAdd,
  onRemove,
  filterForShowing,
  filterForEnabling,
  showCurrentFolder = false,
}: SearchProps) {
  const currentFolderId = useSelector(filesSelectors.getFocusedParent);
  const makeUseOfCurrentFolder =
    showCurrentFolder &&
    currentFolderId !== 'root' &&
    !isShare(currentFolderId);
  const {setShowCurrentFolder} = useCurrentFolder();
  const addedSection = (
    <Fragment>
      {makeUseOfCurrentFolder && (
        <View style={styles.currentFolder}>
          <Typography
            variant="medium"
            size="sm"
            color={theme.colors.neutral.$500}>
            Add Current Folder:
          </Typography>
          <AiChatCurrentFolderChip
            currentFolderId={currentFolderId}
            onChipPress={() => {
              setShowCurrentFolder(true);
            }}
          />
        </View>
      )}
      {files.length > 0 && (
        <Section label="Added">
          {files.map((item, index) => (
            <SearchMenuItem
              key={item.id}
              isEnabled={filterForEnabling?.(item) ?? true}
              onItemPress={() => {
                onRemove(item);
              }}
              {...{
                item,

                instanceId,
                instanceNs,
              }}
            />
          ))}
        </Section>
      )}
    </Fragment>
  );

  return (
    <BaseSearch
      instanceId={instanceId}
      instanceName={instanceName}
      instanceNs={instanceNs}
      instanceAlt={instanceAlt}
      onItemPress={onAdd}
      isSearchOpen={true}
      alwaysShowResults={true}
      addedSection={addedSection}
      filterForShowing={filterForShowing}
      filterForEnabling={filterForEnabling}
      scrollStyles={styles.scroll}
    />
  );
}

const styles = StyleSheet.create({
  scroll: {
    minHeight: 300,
    width: 420,
  },
  currentFolder: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
});
